import * as echarts from "echarts";
import type { EChartsOption } from "echarts";
import { IConnectRender } from "../../definitions/IConnectRender";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";
import { fx_createFincaClimaHorarioGrpcService } from "../../../modulos/clima/services/FincaClimaHorarioGrpcService";
import { FincaClimaDataHorarioResponse } from "../../../modulos/clima/response/FincaClimaDataHorarioResponse";

let CHART_ID = 0;

/** "dia" = horas de hoy (Current). "rango" = días desde hoy en adelante
 *  (Forecast, un punto por día — agregado del horario, ver
 *  `agruparHorarioPorDia`). */
export type ClimaChartModo = "dia" | "rango";

/** Base de los gráficos de tendencia (Current: horas de hoy; Forecast: días
 *  desde hoy en adelante): pide el horario una sola vez al conectar (no se
 *  refresca solo, a diferencia de `ClimaSeccionBase` — es una tendencia, no
 *  un valor puntual), lo transforma (`filterItems`, filtro y/o agregación
 *  según la subclase) e inicializa ECharts con la opción que defina la
 *  subclase. Solo para el dominio clima.
 *  @typeParam TItem Forma del punto del gráfico (hora u día agregado). */
export abstract class ClimaChartBase<TItem extends { fecha: string }>
  implements IConnectRender
{
  protected readonly chartId: number;
  private _chart: echarts.ECharts | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _disposed = false;

  constructor() {
    CHART_ID += 1;
    this.chartId = CHART_ID;
  }

  abstract render(): string;

  connect(): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    fx_createFincaClimaHorarioGrpcService(finca.id)
      .getListAsync()
      .then((items) => {
        if (this._disposed) return;
        this._initChart(this.filterItems(items));
      })
      .catch(() => {});
  }

  dispose(): void {
    this._disposed = true;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._chart?.dispose();
    this._chart = null;
  }

  /** Filtra y/o agrega el horario a los puntos del gráfico (hoy tal cual, o
   *  agrupado por día desde hoy en adelante). */
  protected abstract filterItems(
    items: FincaClimaDataHorarioResponse[],
  ): TItem[];

  /** Opción del gráfico (ejes, series…) a partir de los puntos ya filtrados. */
  protected abstract getOption(items: TItem[]): EChartsOption;

  private _initChart(items: TItem[]): void {
    const el = document.getElementById(`clima-chart-${this.chartId}`);
    if (!el) return;

    this._chart = echarts.init(el);
    this._chart.setOption(this.getOption(items));
    this._resizeObserver = new ResizeObserver(() => this._chart?.resize());
    this._resizeObserver.observe(el);
  }
}
